import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import type { StaticImageData } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { moduleClinical } from 'assets/content/landingPages/supplements';

import { supplementsAllVariants } from '../utils';

import { LineSeparator } from '../LineSeparator';
import { BeforeAfter } from './BeforeAfter';

const Root = styled.section<{
  img: { mobile: StaticImageData; tablet: StaticImageData; desktop: StaticImageData };
}>`
  display: flex;
  flex-direction: column;
  width: 100vw;
  padding: 72px 24px 53px;

  background-image: ${({ img }) => `url(${img.mobile.src})`};
  background-position: top;
  background-size: cover;
  border-bottom: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    display: block;
    padding: 138px 72px 80px;
    background-image: ${({ img }) => `url(${img.tablet.src})`};
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 118px 135px 48px;
    background-image: ${({ img }) => `url(${img.desktop.src})`};
    background-position: right;
  }
`;

/* The title/subtitle container */
const Header = styled.div`
  margin-bottom: 47px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 56px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 64px;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 12px;

  font-size: 36px;
  line-height: 1.17;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    max-width: 500px;
    margin-bottom: 32px;
    font-size: 60px;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;

const Paragraph = styled(Typography)`
  line-height: 1.5;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
    margin-top: 25px;
  }
`;

/* The stats container */
const Statistics = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 40.5px;
  margin-bottom: 39px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-top: 56px;
    margin-bottom: 61px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-top: 71px;
    margin-bottom: 52px;
  }
`;

/* An individual stat/info container */
const Wrapper = styled.div`
  width: 50%;

  &:nth-last-of-type(odd) {
    margin-bottom: 24px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    &:nth-last-of-type(odd) {
      margin-bottom: 72px;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: calc(100% / 3);
  }
`;

const Number = styled(Typography)`
  font-size: 57px;
  font-weight: 300;
  line-height: 1.12;
  letter-spacing: normal;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 100px;
    font-weight: 300;
    text-align: center;
    letter-spacing: normal;
  }
`;

const Info = styled(Typography)`
  font-weight: normal;
  line-height: 1.38;
  letter-spacing: normal;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 24px;
    font-weight: normal;
    line-height: 1.33;
    text-align: center;
    letter-spacing: normal;
  }

  & span {
    display: block;
  }

  & sup {
    top: -0.3em;
  }
`;

const Legend = styled(Typography)`
  letter-spacing: 0.5px;

  &:first-of-type {
    margin-bottom: 14px;
    line-height: 1.5;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    text-align: center;
  }

  ${legacyTheme.breakpoints.up('md')} {
    &:first-of-type {
      margin-bottom: 16px;
    }
  }

  & a {
    font-weight: 600;
    text-decoration: none;
  }
`;

/* To customize the button */
const useStyles = makeStyles({
  clinicalStudy: {
    margin: '0',
    marginLeft: '5px',

    fontFamily: `${legacyTheme.typography.simplonNorm}`,
    fontSize: '12px',
    textTransform: 'initial',
    lineHeight: 0.6,
    letterSpacing: '0.5px',

    '&::after': {
      height: '1px',
    },
  },
});

type ModuleClinicalProps = {
  variant?: (typeof supplementsAllVariants)[number];
};

export const ModuleClinical = ({ variant }: ModuleClinicalProps) => {
  const { classes } = useStyles();
  const testId = variant
    ? `supplements-landing-module-clinical-variant-supplements-${variant}`
    : 'supplements-landing-module';

  return (
    <Root data-testid={testId} img={moduleClinical.image}>
      <Header>
        <Title markupName="p" variant="p1">
          {moduleClinical.title}
        </Title>

        <Paragraph markupName="p" variant="p1">
          {moduleClinical.paragraph}
        </Paragraph>
      </Header>

      <LineSeparator />

      <Statistics>
        {moduleClinical.statistics.map((el, key) => (
          <Wrapper key={String(key)}>
            <Number markupName="p" variant="p1">
              {el.number}
            </Number>

            <Info markupName="p" variant="p1">
              {el.info}
            </Info>
          </Wrapper>
        ))}
      </Statistics>

      {moduleClinical.legends.map((el, key) => (
        <Legend key={String(key)} markupName="p" variant="p3">
          {el.text}
          <Button
            classes={{
              underlinedBlack: classes.clinicalStudy!,
            }}
            Component="a"
            data-testid="supplements-clinical-study"
            href={el.linkPdf}
            target="_blank"
            variant="underlinedBlack"
          >
            {el.linkText}
          </Button>
        </Legend>
      ))}

      <>
        <Spacer size={48} />
        <LineSeparator />
        <BeforeAfter />
      </>
    </Root>
  );
};
